/**
 * USE_KEYBOARD_SOUNDS.JS - Hook pour sons déclenchés au clavier
 * 
 * Ce hook écoute les événements clavier et déclenche automatiquement
 * les sons configurés avec une touche clavier dans SOUNDS_CONFIG.
 * 
 * FONCTIONNALITÉS:
 * - Mapping touches → sons automatique
 * - Évite déclenchement dans inputs/textareas
 * - Cleanup automatique au démontage
 * - Support de multiples touches
 * 
 * CONFIGURATION:
 * Dans config/sounds.js, définir la propriété keyboard:
 * ```javascript
 * horn: {
 *   // ...
 *   keyboard: 'h'  // Touche H pour klaxon
 * }
 * ```
 * 
 * UTILISATION:
 * Appeler une fois dans un composant parent (ex: App.jsx ou Scene.jsx)
 * ```javascript
 * function App() {
 *   useKeyboardSounds()  // Active l'écoute clavier
 *   
 *   return <Canvas>...</Canvas>
 * }
 * ```
 */

import { useEffect } from 'react'
import { useSounds } from './useSounds'
import { SOUNDS_CONFIG } from '../config/sounds'

/**
 * Hook useKeyboardSounds
 * 
 * Écoute les événements clavier et joue les sons associés.
 * Doit être appelé une seule fois dans un composant parent.
 * 
 * EXEMPLE:
 * ```javascript
 * function Scene() {
 *   useKeyboardSounds()  // Active les sons clavier
 *   
 *   return (
 *     <>
 *       <OrbitControls />
 *       <mesh>...</mesh>
 *     </>
 *   )
 * }
 * ```
 */
export function useKeyboardSounds() {
  const { play } = useSounds()

  useEffect(() => {
    // ============================================
    // CONSTRUCTION DU MAPPING TOUCHES → SONS
    // ============================================

    // Créer un dictionnaire: touche → nom du son
    const keyToSound = {}
    
    Object.entries(SOUNDS_CONFIG).forEach(([soundName, config]) => {
      if (config.keyboard) {
        // Normaliser en minuscule
        const key = config.keyboard.toLowerCase()
        keyToSound[key] = soundName
      }
    })

    // Nombre de sons avec touche clavier
    const keyCount = Object.keys(keyToSound).length
    
    if (keyCount === 0) {
      return
    }

    // ============================================
    // GESTIONNAIRE D'ÉVÉNEMENT CLAVIER
    // ============================================

    /**
     * Gestionnaire keydown
     * 
     * @param {KeyboardEvent} event - Événement clavier
     */
    const handleKeyDown = (event) => {
      // ============================================
      // 1. VÉRIFIER SI DANS INPUT/TEXTAREA
      // ============================================

      // Ne pas déclencher si focus dans un champ de saisie
      const tagName = event.target.tagName
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return
      }

      // ============================================
      // 2. VÉRIFIER SI TOUCHE EST MAPPÉE
      // ============================================

      // Normaliser la touche en minuscule
      const key = event.key.toLowerCase()

      // Chercher le son associé
      const soundName = keyToSound[key]

      if (!soundName) {
        // Pas de son pour cette touche
        return
      }

      // ============================================
      // 3. EMPÊCHER COMPORTEMENT PAR DÉFAUT
      // ============================================

      // Éviter scroll, etc.
      event.preventDefault()

      // ============================================
      // 4. JOUER LE SON
      // ============================================

      const played = play(soundName)

      if (played) {
        console.log(`🎹 Touche ${key.toUpperCase()} → ${soundName}`)
      }
    }

    // ============================================
    // ATTACHER L'ÉCOUTEUR
    // ============================================

    window.addEventListener('keydown', handleKeyDown)

    // ============================================
    // CLEANUP AU DÉMONTAGE
    // ============================================

    return () => {
      console.log('⌨️ Désactivation des sons clavier')
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [play])
}

/**
 * NOTES D'UTILISATION:
 * 
 * 1. OÙ APPELER CE HOOK:
 *    - Dans un composant parent (App, Scene)
 *    - Une seule fois dans l'arbre des composants
 *    - Pas besoin dans chaque composant
 * 
 * 2. CONFIGURATION DES TOUCHES:
 *    Dans config/sounds.js:
 *    ```javascript
 *    horn: {
 *      // ...
 *      keyboard: 'h'
 *    },
 *    engine: {
 *      // ...
 *      keyboard: 'e'
 *    }
 *    ```
 * 
 * 3. TOUCHES SPÉCIALES:
 *    - Utilisez event.key pour touches spéciales
 *    - Exemples: 'ArrowUp', 'ArrowDown', 'Escape', etc.
 *    - Voir: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
 * 
 * 4. CONFLITS DE TOUCHES:
 *    - Éviter touches déjà utilisées (M pour mute)
 *    - Éviter touches système (F1-F12, etc.)
 *    - Préférer lettres simples (A-Z)
 * 
 * 5. DEBUG:
 *    Les messages console affichent:
 *    - Touches activées au montage
 *    - Touches pressées et sons joués
 * 
 * EXEMPLE COMPLET:
 * ```javascript
 * // Dans config/sounds.js
 * export const SOUNDS_CONFIG = {
 *   horn: {
 *     files: ['/sounds/horns/horn-1.mp3'],
 *     minDelta: 500,
 *     // ...
 *     keyboard: 'h'  // Touche H
 *   },
 *   bell: {
 *     files: ['/sounds/bells/bell-1.mp3'],
 *     minDelta: 300,
 *     // ...
 *     keyboard: 'b'  // Touche B
 *   }
 * }
 * 
 * // Dans App.jsx ou Scene.jsx
 * function App() {
 *   useKeyboardSounds()  // Active H et B
 *   
 *   return <Canvas>...</Canvas>
 * }
 * ```
 */

